<template>
  <div id="register">
    
    <div class="ambient-background">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
      <div class="noise-overlay"></div> </div>

    <transition name="fade-slide" appear>

      <div class="main-container">
        
        <header class="header-section">
          <h1 class="hero-title">{{ title }}</h1>
        </header>

        <div class="ai-border-wrapper">
          <div class="glass-surface">
            
            <div class="login-type-switch">
              <span :class="{ active: loginMode === 'sms' }" @click="loginMode = 'sms'">验证码登录</span>
              <span class="divider">|</span>
              <span :class="{ active: loginMode === 'password' }" @click="loginMode = 'password'">密码登录</span>
            </div>

            <van-form @submit="onSubmit" ref="form" :show-error-message="false">
              
              <div class="list-group">
                
                <div class="list-item">
                  <div class="label-wrap">学校</div>
                  <div class="input-wrap" @click="schoolShowPicker = true">
                    <van-field placeholder="请搜索选择学校（如：南京）" readonly class="ios-field" v-model="schoolName" is-link @click="schoolShowPicker = true"/>
                  </div>
                </div>

                <div class="list-item">
                  <div class="label-wrap">手机号</div>
                  <div class="input-wrap">
                    <van-field
                      v-model="formData.mobile"
                      type="tel"
                      placeholder="请输入手机号码"
                      :border="false"
                      clearable
                      class="ios-field"
                      :rules="[{ required: true }]"
                    />
                  </div>
                </div>

                <div class="list-item" v-if="loginMode === 'sms'">
                  <div class="label-wrap">验证码</div>
                  <div class="input-wrap code-layout">
                    <van-field
                      v-model="formData.smsCode"
                      type="digit"
                      placeholder="6位数字"
                      :border="false"
                      class="ios-field code-field"
                      maxlength="6"
                      :rules="[{ required: true }]"
                    />
                    <div class="vertical-divider"></div>
                    <div class="send-btn-container">
                      <SendMsgCode
                        :phoneNumber='formData.mobile'
                        :data="formData"
                        class="custom-send-btn"
                      />
                    </div>
                  </div>
                </div>

                <div class="list-item" v-if="loginMode === 'password'">
                  <div class="label-wrap">密码</div>
                  <div class="input-wrap">
                    <van-field
                      v-model="formData.password"
                      type="password"
                      placeholder="请输入密码"
                      :border="false"
                      clearable
                      class="ios-field"
                      :rules="[{ required: true }]"
                    />
                  </div>
                </div>

              </div>

              <div class="action-area">
                <button 
                  type="submit" 
                  class="apple-btn primary" 
                  :class="{ 'btn-loading': loading }" 
                  :disabled="loading"
                >
                  <span v-if="!loading">确认绑定</span>
                  <span v-else class="loading-spinner"></span>
                </button>
                
                <p class="security-tip">
                  <i class="shield-icon"></i> 信息已加密保护
                </p>
              </div>

            </van-form>
          </div>
        </div>

      </div>

    </transition>

    <van-popup v-model="schoolShowPicker" round position="bottom" :style="{ height: '55%' }">
      <van-search v-model="searchvalue" placeholder="请输入学校名称关键字搜索（如：南京）" @search="onSearchConfirm" />
      <van-picker :columns="schoolColumns" :default-index="defaultIndex" show-toolbar @cancel="schoolShowPicker = false"
        @confirm="onConfirmSchool" />
    </van-popup>

  </div>
</template>

<script>
import { Notify } from "vant";
// 新增引入 mobileBindPassword 接口
import { mobileBind, mobileBindPassword, checkPersonType, loginByOpenId } from "@/api/bind";
import { getTeacherSchoolList } from "@/api/common";
import { setLocal, removeLocal, getLocal } from "@/utils/cookie";
import SendMsgCode from "@/components/SendMsgCode.vue";
import wx from 'weixin-js-sdk';

export default {
  name: "Register",
  components: { SendMsgCode },
  data() {
    return {
      title: '身份绑定',
      loading: false, // 按钮加载状态
      loginMode: 'sms', // 'sms' 验证码登录 | 'password' 密码登录
      
      // 表单数据
      formData: {
        mobile: '',
        smsCode: '',
        password: '', // 新增密码字段
        personType: ''
      },

      // 学校选择相关
      schoolName: "",
      schoolId: "",
      searchvalue: "",
      schoolShowPicker: false,
      schoolColumns: [],
      columnsAll: [],
      defaultIndex: '',
      // 搜索防抖定时器与请求序号（防止响应乱序覆盖结果）
      searchTimer: null,
      searchSeq: 0,
    };
  },
  watch: {
    searchvalue(val) {
      this.onSearchInput(val);
    }
  },
  created() {
    const identity = this.$route.query.identity;
    // 细化标题，增加亲和力
    if (identity == 'parent') {
      this.formData.personType = '8';
      this.title = '家长绑定';
    } else if (identity == 'teacher') {
      this.formData.personType = '1';
      this.title = '教师绑定';
    }
  },
  mounted() {
    this.schoolColumns = [];
    this.init();
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
  },
  methods: {
    // 输入防抖：停止输入 300ms 后才发请求，避免逐字请求风暴
    onSearchInput(val) {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      const kw = (val || '').trim();
      if (!kw) {
        this.schoolColumns = [];
        return;
      }
      this.searchTimer = setTimeout(() => {
        this.fetchSchools(kw);
      }, 300);
    },

    // 键盘“完成/搜索”键兜底触发（部分输入法上屏不触发 watch）
    onSearchConfirm(val) {
      const kw = ((val || this.searchvalue || '') + '').trim();
      if (!kw) return;
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      this.fetchSchools(kw);
    },

    fetchSchools(keyword) {
      if (!keyword || !keyword.trim()) {
        this.schoolColumns = [];
        return;
      }
      const seq = ++this.searchSeq;
      getTeacherSchoolList({ keyword: keyword.trim() }).then((res) => {
        if (seq !== this.searchSeq) return; // 旧响应直接丢弃，只采纳最后一次搜索
        const body = res && res.data && Array.isArray(res.data.body) ? res.data.body : null;
        if (body) {
          this.schoolColumns = body.map((item) => ({
            text: item.name || item.schName || '',
            value: item.code || item.schCode || ''
          })).filter(i => i.text && i.value);
        } else {
          // 响应格式异常（弱网报文损坏等），不能静默吞掉
          this.schoolColumns = [];
          Notify({ type: 'warning', message: '学校数据加载异常，请重新搜索' });
        }
      }).catch(err => {
        if (seq !== this.searchSeq) return;
        console.warn("获取学校失败", err);
        this.schoolColumns = [];
      });
    },

    onConfirmSchool(val) {
      this.schoolName = val.text;
      this.schoolId = val.value;
      this.schoolShowPicker = false;
    },

    async init() {
      const openId = getLocal('openId');
      if(!openId) return;

      // 并发请求优化加载速度
      try {
        const [checkRes, loginRes] = await Promise.all([
          checkPersonType({ openId }),
          loginByOpenId({ openId })
        ]);

        // 处理身份状态
        const status = +checkRes.data.body;
        if ([-2].includes(status) || (status === 1 && this.formData.personType == 1) || (status === 8 && this.formData.personType == 8)) {
           this.$router.replace(`/personalInfo`);
        }

        // 缓存登录信息
        if(loginRes.data.body) {
          setLocal("userInfo", JSON.stringify(loginRes.data.body));
          setLocal("Authorization", loginRes.data.body.token);
        }

      } catch (error) {
        console.error("Init failed", error);
      }
    },

    onSubmit() {
      if(!this.schoolId || this.schoolId == '') {
        Notify({ type: "warning", message: '请选择学校！' });
        return;
      }
      if(!this.formData.mobile) {
        Notify({ type: "warning", message: '请输入正确的手机号' });
        return;
      }

      // 根据模式进行分支校验
      if (this.loginMode === 'sms') {
        if(!this.formData.smsCode || this.formData.smsCode.length < 4) {
          Notify({ type: "warning", message: '请输入验证码' });
          return;
        }
      } else {
        if(!this.formData.password) {
          Notify({ type: "warning", message: '请输入密码' });
          return;
        }
      }

      this.loading = true; // 开启加载动画
      
      // 公共请求参数
      let reqData = { 
        mobile: this.formData.mobile,
        personType: this.formData.personType,
        openId: getLocal('openId'), 
        schoolCode: this.schoolId
      };

      let submitPromise;

      // 根据模式选择调用的接口
      if (this.loginMode === 'sms') {
        reqData.smsCode = this.formData.smsCode;
        submitPromise = mobileBind(reqData);
      } else {
        reqData.password = this.formData.password;
        submitPromise = mobileBindPassword(reqData);
      }
      
      submitPromise.then(async (res) => {
        Notify({ 
          type: "success", 
          message: '绑定成功', 
          duration: 1500,
        });
        console.log("bindSuccess", res);
        
        if(res.data.body) {
          setLocal("userInfo", JSON.stringify(res.data.body));
          setLocal("Authorization", res.data.body.token);
        }
        await this.changeUSER();

        setTimeout(() => {
          if (this.$route.query.backUrl) {
            this.$router.replace(this.$route.query.backUrl);
          } else {
            wx.closeWindow();
          }
        }, 1500);
      }).catch(err => {
         Notify({ type: "danger", message: err.message || '绑定失败' });
         this.loading = false;
      });
    },

    async changeUSER() {
      await loginByOpenId({
        openId: getLocal('openId'), schoolCode: this.schoolId
      }).then(res => {
        console.log(res);
        if (res.data.obj == "personBindErrorCode") {
          // 异常情况处理
        } else {
          setLocal("userInfo", JSON.stringify(res.data.body));
          setLocal("Authorization", res.data.body.token);
        }
      }).catch((error) => {
        console.log(error);
      });
    },
  },
};
</script>

<style lang="less" scoped>
/* ========== 全局重置与字体 ========== */
#register {
  width: 100%;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  /* 使用 Apple 系统字体栈 */
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "PingFang SC", "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased; /*字体抗锯齿*/
  color: #1d1d1f;
}

/* ========== 1. 高级感动态背景 ========== */
.ambient-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 0;
  background: #fbfbfd; /* 极淡的灰白底色 */
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px); /* 更大的模糊半径 */
  opacity: 0.5;
}

/* 调整配色，使用更高级的莫兰迪/Mac OS 壁纸色系 */
.orb-1 {
  width: 60vw;
  height: 60vw;
  background: #a2d2ff;
  top: -10%;
  left: -10%;
  animation: breathe 10s infinite ease-in-out;
}

.orb-2 {
  width: 50vw;
  height: 50vw;
  background: #ffafcc;
  bottom: -5%;
  right: -5%;
  animation: breathe 12s infinite ease-in-out reverse;
}

.orb-3 {
  width: 40vw;
  height: 40vw;
  background: #cdb4db;
  top: 40%;
  left: 30%;
  animation: rotate 20s infinite linear;
}

/* 噪点遮罩：增加磨砂质感，防止色带 */
.noise-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.03'/%3E%3C/svg%3E");
  pointer-events: none;
}

/* ========== 2. 布局与排版 ========== */
.main-container {
  position: relative;
  z-index: 1;
  padding: 12vh 24px 40px; /* 顶部留白更多，更有呼吸感 */
  max-width: 600px;
  margin: 0 auto;
}

.header-section {
  margin-bottom: 20px;
  animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  
  .hero-title {
    font-size: 22px;
    font-weight: 600;
    letter-spacing: -0.5px;
    color: #000;
    margin-bottom: 12px;
    padding-left: 10px;
  }
}

/* ========== 3. 极致磨玻璃卡片 ========== */
.glass-surface {
  /* 核心 Glassmorphism 代码 */
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  border-radius: 24px;
  padding: 32px 20px;
  animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.1s backwards;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15); 
}

/* 登录方式切换器 */
.login-type-switch {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  font-size: 16px;
  color: #86868b;
  
  span {
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 4px 8px;
    &.active {
      color: #000;
      font-weight: 600;
    }
  }
  
  .divider {
    margin: 0 16px;
    color: rgba(60, 60, 67, 0.18);
    cursor: default;
  }
}

.list-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 0.5px solid rgba(60, 60, 67, 0.10); /* iOS 标准分割线颜色 */
  
  &:last-child {
    border-bottom: none;
  }
  
  .label-wrap {
    width: 70px;
    font-size: 17px;
    font-weight: 500;
    color: #000;
    flex-shrink: 0;
  }
  
  .input-wrap {
    flex: 1;
    position: relative;
    
    &.code-layout {
      display: flex;
      align-items: center;
    }
  }
}

/* 深度定制 Vant Field，使其隐形 */
.ios-field {
  padding: 0 !important;
  background: transparent !important;
  
  ::v-deep .van-field__control {
    font-size: 17px;
    color: #000;
    height: 24px;
    line-height: 24px;
    
    &::placeholder {
      color: #3c3c434d; /* iOS 标准占位符颜色 */
    }
  }

  ::v-deep .van-cell__title{
    font-size: 17px;
    color: #000;
    &::placeholder {
      color: #3c3c434d;
    }
  }
}

/* 验证码特殊样式 */
.code-field {
  width: 120px !important;
  flex: none !important;
}

.vertical-divider {
  width: 1px;
  height: 18px;
  background: rgba(60, 60, 67, 0.18);
  margin: 0 12px;
}

.send-btn-container {
  flex: 1;
  text-align: right;
  
  /* 强制覆盖组件样式 */
  ::v-deep button, ::v-deep span {
    color: #007aff !important;
    font-size: 16px !important;
    font-weight: 400 !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    
    &.disabled {
      color: #3c3c434d !important;
    }
  }
}

/* ========== 4. 按钮与反馈 ========== */
.action-area {
  margin-top: 40px;
}

.apple-btn {
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 14px; /* iOS 15+ 风格大圆角 */
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.4px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.25, 1, 0.5, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &.primary {
    /* 苹果黑，增加立体感 */
    background: #000; 
    color: #fff;
    box-shadow: 0 4px 12px rgba(0, 122, 255, 0.25);
    
    &:active {
      transform: scale(0.97);
      box-shadow: 0 2px 8px rgba(0, 122, 255, 0.2);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

/* 按钮 Loading 动画 */
.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.security-tip {
  margin-top: 24px;
  text-align: center;
  font-size: 12px;
  color: #86868b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  .shield-icon {
    width: 12px;
    height: 14px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2386868b'%3E%3Cpath d='M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z'/%3E%3C/svg%3E");
    background-size: cover;
  }
}

/* ========== 5. 弹窗样式重写 (全磨玻璃) ========== */
.ios-popup {
  background: rgba(245, 245, 247, 0.85) !important;
  backdrop-filter: blur(30px) !important;
  -webkit-backdrop-filter: blur(30px);
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 0.5px solid rgba(0,0,0,0.1);
  
  .cancel-btn {
    color: #007aff;
    font-size: 16px;
  }
  .popup-title {
    font-weight: 600;
    font-size: 17px;
  }
  .confirm-dummy {
    width: 32px;
  }
}

.ios-search {
  padding: 12px 16px;
  background: transparent !important;
  
  ::v-deep .van-search__content {
    background: rgba(118, 118, 128, 0.12);
  }
}

/* 动画定义 */
@keyframes slideUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.fade-slide-enter-active {
  transition: all 0.8s ease;
}
.fade-slide-enter {
  opacity: 0;
  transform: translateY(20px);
}
</style>